import React, {useState, useEffect, useCallback, useRef} from "react";
import {AgGridReact} from "ag-grid-react";
import {
    ClientSideRowModelModule,
    LicenseManager,
    ModuleRegistry,
    RowGroupingModule,
} from "ag-grid-enterprise";
import {Button, Box, Typography, Menu, MenuItem} from "@material-ui/core";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import {Link as ReactLink, useNavigate, useParams} from "react-router-dom";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import {toast} from "react-toastify";
import {
    getCFSFields,
    getTemplates,
    createTemplate,
    reArrangeTemplates,
    updateTemplate,
} from "../../api";

import "../../App.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "react-toastify/dist/ReactToastify.css";
import TemplateDialog from "../../components/templateDialog";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import fnOpenToast from "../../components/toast";
import RedoIcon from "@material-ui/icons/Redo";
import {IconButton} from "@material-ui/core";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";

ModuleRegistry.registerModules([ClientSideRowModelModule, RowGroupingModule]);
LicenseManager.setLicenseKey(
    "Using_this_{AG_Grid}_Enterprise_key_{AG-062453}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Lucid_USA,_Inc}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{V360}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{V360}_need_to_be_licensed___{V360}_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_August_2025}____[v3]_[01]_MTc1NjMzNTYwMDAwMA==83cd4edac4587e17c3187cf63f89d914",
);
toast.configure();

const initialData = {
    _id: {
        value: null,
        hasError: false,
    },
    cfsTemplateFields: {
        value: [
            {
                docusignField: "",
                cfsField: {},
            },
        ],
        hasError: false,
    },
    isActive: {
        value: true,
        hasError: false,
    },
    docusignTemplateId: {
        value: "",
        hasError: false,
    },
    name: {
        value: "",
        hasError: false,
    },
    language: {
        value: 'en',
        hasError: false,
    }
};

const ActiveIndicator = ({value}) => {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
            }}
        >
            {value ? (
                <CheckCircleOutlineIcon style={{color: "green"}}/>
            ) : (
                <CancelIcon style={{color: "red"}}/>
            )}
        </div>
    );
};

function DocusignTemplates() {
    const {country: currentCountry} = useParams();
    const gridRef = useRef();
    const [gridApi, setGridApi] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [templatesData, setTemplatesData] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [cfsFields, setCfsFields] = useState([]);
    const [formData, setFormData] = useState(initialData);
    const [open, setOpen] = useState(false);
    const [rootNodes, setRootNodes] = useState([]);
    const [isRearrangeEnabled, setIsRearrangeEnabled] = useState(false);
    const [country, setCountry] = useState(currentCountry);

    const navigate = useNavigate();

    const columnDefs = [
        {
            headerName: "Docusign Template ID",
            field: "docusignTemplateId",
            flex: 3,
            cellRenderer: (params) => {
                return params.node.group ? params.value : "";
            },
        },
        {
            headerName: "Docusign Field Name",
            field: "docusignFieldName",
            flex: 1,
            filter: false,
            cellRenderer: (params) =>
                params.node.group ? "" : params.value || "",
        },
        {
            headerName: "CFS Field Name",
            field: "cfsFieldName",
            flex: 1,
            filter: false,
            cellRenderer: (params) =>
                params.node.group ? "" : params.value?.name || "",
            valueGetter: (params) => {
                if (params.data.cfsFieldName) {
                    return (
                        cfsFields.find(
                            (field) => field.id === params.data.cfsFieldName.id,
                        ) || params.data.cfsFieldName
                    );
                }
                return null;
            },
        },
        {
            headerName: "Language",
            field: "language",
            flex: 1,
            filter: false,
            hide: country === "US",
        },
        {
            headerName: "Active",
            field: "isActive",
            flex: 1,
            filter: false,
            cellRenderer: (params) => {
                if (!params.node.group) return null;
                return <ActiveIndicator value={params.value}/>;
            },
        },
        {
            headerName: "Edit",
            width: 100,
            filter: false,
            sortable: false,
            suppressMenu: true,
            cellRenderer: (params) => {
                if (params.node.group) {
                    return (
                        <IconButton
                            size="small"
                            onClick={() => {
                                updateInitialData(params.data.id);
                                setOpen(true);
                            }}
                        >
                            <EditIcon fontSize="small"/>
                        </IconButton>
                    );
                }
                return null;
            },
        },
        {
            headerName: "Rearrange",
            width: 120,
            cellRenderer: (params) => {
                if (params.node.group && isRearrangeEnabled) {
                    return (
                        <div>
                            <IconButton
                                size="small"
                                onClick={() =>
                                    handleMoveRow(params.data.id, "up")
                                }
                            >
                                <ArrowUpwardIcon fontSize="small"/>
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() =>
                                    handleMoveRow(params.data.id, "down")
                                }
                            >
                                <ArrowDownwardIcon fontSize="small"/>
                            </IconButton>
                        </div>
                    );
                }
                return null;
            },
            sortable: false,
            filter: false,
            suppressMenu: true,
        },
    ];

    const refreshData = useCallback(async (selectedCountry) => {
        const templatesData = await getTemplates(selectedCountry);
        setTemplatesData(templatesData);
        const cfsFields = await getCFSFields();
        setCfsFields(cfsFields);
        const templatesDataFormatted = templatesData.map((template) => {
            const children =
                Object.keys(template.cfsTemplateFields).length > 0
                    ? Object.entries(template.cfsTemplateFields).map(
                        ([key, value]) => {
                            const cfsField = cfsFields.find(
                                (field) => field.id === value._id,
                            );
                            return {
                                id: `${template._id}-${key}`,
                                cfsFieldName: cfsField || {
                                    id: value._id,
                                    name: value.name,
                                },
                                docusignFieldName: key,
                                treePath: [template.name, key],
                                rowId: `${template._id}-${key}-${Math.random().toString(36).substring(7)}`,
                                docusignTemplateId:
                                template.docusignTemplateId,
                                isActive: template.isActive,
                            };
                        },
                    )
                    : //necessary to show an empty row when there are no cfs fields
                    [
                        {
                            id: `${template._id}-empty`,
                            cfsFieldName: {name: ""},
                            docusignFieldName: "",
                            treePath: [template.name, ""],
                            rowId: `${template._id}-empty-${Math.random().toString(36).substring(7)}`,
                            docusignTemplateId: template.docusignTemplateId,
                            isActive: template.isActive,
                        },
                    ];

            return {
                id: template._id,
                rowId: `${template._id}-${template.name}`,
                name: template.name,
                treePath: [template.name],
                docusignTemplateId: template.docusignTemplateId,
                isActive: template.isActive,
                children,
                language: template.language
            };
        });
        setRootNodes(templatesDataFormatted);
        setTableData(
            templatesDataFormatted.flatMap((template) => [
                template,
                ...template.children,
            ]),
        );
    }, []);

    const onGridReady = useCallback((params) => {
        setGridApi(params.api);
    }, []);

    useEffect(() => {
        refreshData(country);
    }, [refreshData, country]);

    const handleSave = async (e) => {
        try {
            e.preventDefault();
            const requestBody = {
                _id: formData._id.value,
                cfsTemplateFields: formData.cfsTemplateFields.value.reduce(
                    (acc, field) => {
                        acc[field.docusignField] = field.cfsField._id;
                        return acc;
                    },
                    {},
                ),
                isActive: Boolean(formData.isActive.value),
                docusignTemplateId: formData.docusignTemplateId.value,
                name: formData.name.value,
                country,
                language: formData.language.value || 'en',
            };

            const isUpdate = formData._id.value;
            isUpdate
                ? await updateTemplate(requestBody)
                : await createTemplate(requestBody);
            fnOpenToast(
                `Template ${isUpdate ? "Updated" : "Created"} Successfully`,
                "success",
            );
            refreshData(country);
            handleClose();
        } catch (e) {
            fnOpenToast("Error ", "error");
        }
    };

    const handleSetCountry = async(event) => {
        const countrySelected = event.target.value;
        setCountry(countrySelected);
        navigate(`/templates-management/docusign-templates/${countrySelected}`);
    };

    const handleMoveRow = useCallback(
        (id, direction) => {
            setRootNodes((prevNodes) => {
                const index = prevNodes.findIndex((node) => node.id === id);
                if (index === -1) return prevNodes;

                const newNodes = [...prevNodes];
                const [movedNode] = newNodes.splice(index, 1);

                if (direction === "up" && index > 0) {
                    newNodes.splice(index - 1, 0, movedNode);
                } else if (
                    direction === "down" &&
                    index < prevNodes.length - 1
                ) {
                    newNodes.splice(index + 1, 0, movedNode);
                } else {
                    newNodes.splice(index, 0, movedNode);
                }

                const flattenedData = newNodes.flatMap((template) => [
                    template,
                    ...template.children,
                ]);
                setTableData(flattenedData);
                if (gridApi) {
                    gridApi.setRowData(flattenedData);
                }

                return newNodes;
            });
        },
        [gridApi],
    );

    const handleActionClick = useCallback(
        (event) => setAnchorEl(event.currentTarget),
        [],
    );
    const handleActionClose = useCallback(() => setAnchorEl(null), []);
    const handleEnableRearrange = useCallback(() => {
        setIsRearrangeEnabled(true);
        setAnchorEl(null);
    }, []);

    const handleSaveChanges = async () => {
        const confirm = window.confirm(
            "Are you sure you want to save the changes? " +
            "This will affect the docusign templates order in the PA package.",
        );
        if (!confirm) {
            return;
        }
        try {
            await reArrangeTemplates({
                sortedIds: rootNodes.map(
                    (node) => node.id,
                ),
                country
            });
            toast.success("Changes saved successfully!", {autoClose: 3000});
            setAnchorEl(null);
        } catch (error) {
            toast.error("Error saving changes", {autoClose: 3000});
        }
        setIsRearrangeEnabled(false);
        setAnchorEl(null);
    };

    const handleCancelChanges = useCallback(() => {
        refreshData(country);
        setIsRearrangeEnabled(false);
        setAnchorEl(null);
    }, [refreshData]);

    const handleAddTemplate = useCallback(() => {
        setFormData(initialData);
        setOpen(true);
    }, []);

    useEffect(() => {
        if (gridApi) {
            const flattenedData = rootNodes.flatMap((template) => [
                template,
                ...template.children,
            ]);
            gridApi.setRowData(flattenedData);
        }
    }, [rootNodes, gridApi]);

    const defaultColDef = {
        sortable: true,
        resizable: true,
        editable: false,
    };

    const gridOptions = {
        columnDefs,
        defaultColDef,
        rowData: tableData,
        treeData: true,
        groupDefaultExpanded: 0,
        getDataPath: (data) => {
            return data.treePath || [data.name];
        },
        autoGroupColumnDef: {
            headerName: "Template Name",
            minWidth: 300,
            flex: 2,
            cellRenderer: "agGroupCellRenderer",
            cellRendererParams: {
                suppressCount: true,
                innerRenderer: (params) =>
                    params.node.group ? params.value : "",
            },
            showRowGroup: true,
            field: "name",
            lockPosition: true,
            lockVisible: true,
        },
        getRowId: (params) => params.data.rowId,
        suppressAutoSize: true,
    };

    const updateInitialData = (templateId) => {
        const template = templatesData.find(
            (template) => template._id === templateId,
        );
        setFormData({
            ...initialData,
            _id: {
                value: template._id,
                hasError: false,
            },
            cfsTemplateFields: {
                value: Object.entries(template.cfsTemplateFields).map(
                    ([key, value]) => ({
                        docusignField: key,
                        cfsField: value,
                    }),
                ),
                hasError: false,
            },
            isActive: {
                value: Boolean(template.isActive),
                hasError: false,
            },
            docusignTemplateId: {
                value: template.docusignTemplateId,
                hasError: false,
            },
            name: {
                value: template.name,
                hasError: false,
            },
            language: {
                value: template.language || 'en',
                hasError: false,
            }
        });
    };
    const handleClose = () => {
        setOpen(false);
        setFormData(initialData);
    };

    const onChange = useCallback((e) => {
        const {id, value, index} = e.target;

        setFormData((prevData) => {
            switch (id) {
                case "addCfsTemplateField":
                    return {
                        ...prevData,
                        cfsTemplateFields: {
                            ...prevData.cfsTemplateFields,
                            value: [
                                ...prevData.cfsTemplateFields.value,
                                {cfsField: "", docusignField: ""},
                            ],
                        },
                    };

                case "deleteCfsTemplateField":
                    return {
                        ...prevData,
                        cfsTemplateFields: {
                            ...prevData.cfsTemplateFields,
                            value: prevData.cfsTemplateFields.value.filter(
                                (_, i) => i !== index,
                            ),
                        },
                    };

                case "cfsField":
                    return {
                        ...prevData,
                        cfsTemplateFields: {
                            ...prevData.cfsTemplateFields,
                            value: prevData.cfsTemplateFields.value.map(
                                (field, i) =>
                                    i === index
                                        ? {...field, cfsField: value}
                                        : field,
                            ),
                        },
                    };

                case "docusignField":
                    return {
                        ...prevData,
                        cfsTemplateFields: {
                            ...prevData.cfsTemplateFields,
                            value: prevData.cfsTemplateFields.value.map(
                                (field, i) =>
                                    i === index
                                        ? {...field, [id]: value}
                                        : field,
                            ),
                        },
                    };

                case "isActive":
                case "language":
                    return {
                        ...prevData,
                        [id]: {
                            ...prevData[id],
                            value: value,
                        },
                    };

                default:
                    return {
                        ...prevData,
                        [id]: {...prevData[id], value},
                    };
            }
        });
    }, []);

    return (
        <div>
            <Box margin="10px 20px">
                <Breadcrumbs aria-label="breadcrumb">
                    <ReactLink
                        color="inherit"
                        to="/"
                        className="breadcrumb-link"
                    >
                        Dashboard
                    </ReactLink>
                    <ReactLink
                        color="inherit"
                        to="/templates-management"
                        className="breadcrumb-link"
                    >
                        Templates
                    </ReactLink>
                    <Typography color="textPrimary">
                        Docusign Templates
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box
                display="flex"
                justifyContent="space-between"
                margin="10px 20px"
            >
                <Typography
                    variant="h5"
                    component="h5"
                    style={{
                        fontFamily: "GTAmericaMedium",
                        fontSize: "24px",
                        lineHeight: "120%",
                        fontWeight: "500",
                    }}
                >
                    Docusign Templates
                </Typography>

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <div style={{marginRight: "10px"}}>
                        <FormControl variant="outlined">
                            <InputLabel id="demo-simple-select-autowidth-label">
                                Country
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-outlined-label"
                                id="demo-simple-select-outlined"
                                value={country}
                                onChange={handleSetCountry}
                                label="Country"
                            >
                                <MenuItem value={"US"}>US</MenuItem>
                                <MenuItem value={"CA"}>CA</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleActionClick}
                    >
                        Actions <ExpandMoreIcon/>
                    </Button>
                    <Menu
                        id="action-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleActionClose}
                    >
                        <MenuItem onClick={handleAddTemplate}>
                            <AddCircleOutlineIcon
                                fontSize="small"
                                style={{marginRight: "5px"}}
                            />{" "}
                            Add Template
                        </MenuItem>
                        {!isRearrangeEnabled ? (
                            <MenuItem onClick={handleEnableRearrange}>
                                <DragIndicatorIcon
                                    fontSize="small"
                                    style={{marginRight: "5px"}}
                                />{" "}
                                Enable Rearrange
                            </MenuItem>
                        ) : (
                            <>
                                <MenuItem onClick={handleSaveChanges}>
                                    <CheckCircleOutlineIcon
                                        fontSize="small"
                                        style={{marginRight: "5px"}}
                                    />{" "}
                                    Save Changes
                                </MenuItem>
                                <MenuItem onClick={handleCancelChanges}>
                                    <RedoIcon
                                        fontSize="small"
                                        style={{marginRight: "5px"}}
                                    />{" "}
                                    Cancel Changes
                                </MenuItem>
                            </>
                        )}
                    </Menu>
                </Box>
            </Box>
            <div
                style={{
                    margin: "0 20px",
                    height: "calc(100vh - 170px)",
                    border: "1px solid #babfc7",
                }}
            >
                <div
                    className="ag-theme-alpine"
                    style={{height: "100%", width: "100%"}}
                >
                    <AgGridReact
                        {...gridOptions}
                        onGridReady={onGridReady}
                        ref={gridRef}
                        rowSelection="multiple"
                    />
                </div>
                <TemplateDialog
                    open={open}
                    data={formData}
                    handleClose={handleClose}
                    cfsFieldOptions={cfsFields}
                    onChange={onChange}
                    isEditing={formData._id.value !== null}
                    handleFormSubmit={handleSave}
                />
            </div>
        </div>
    );
}

export default DocusignTemplates;
